import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Palette, 
  Image, 
  Type, 
  Globe, 
  Mail, 
  Building2, 
  Eye, 
  Upload, 
  Trash2, 
  RotateCcw,
  Check,
  X,
  Crown,
  Lock,
  Users,
  Plus,
  Settings,
  ExternalLink,
  Copy,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhiteLabelSettings = () => {
  const [activeTab, setActiveTab] = useState('branding');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasAgencyPlan, setHasAgencyPlan] = useState(false);
  
  // Form states
  const [companyName, setCompanyName] = useState('');
  const [colors, setColors] = useState({
    primary: '#10b981',
    secondary: '#059669',
    accent: '#34d399'
  });
  const [loginPage, setLoginPage] = useState({
    welcome_message: 'Welcome back',
    background_color: '#0f172a',
    button_color: '#10b981'
  });
  const [emailSettings, setEmailSettings] = useState({
    from_name: 'Team AILeadMantra',
    footer_text: '© 2026 AILeadMantra. All rights reserved.',
    use_custom_colors: false
  });
  const [agencySettings, setAgencySettings] = useState({
    show_powered_by: true,
    is_whitelabel_enabled: false
  });
  
  // Client management
  const [clients, setClients] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ client_name: '', client_email: '' });
  
  // File refs
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const emailLogoInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchClients();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setHasAgencyPlan(data.has_agency_plan);
        setCompanyName(data.company_name || '');
        setColors(data.colors || { primary: '#10b981', secondary: '#059669', accent: '#34d399' });
        setLoginPage(data.login_page || { welcome_message: 'Welcome back', background_color: '#0f172a', button_color: '#10b981' });
        setEmailSettings(data.email || { from_name: 'Team AILeadMantra', footer_text: '© 2026 AILeadMantra. All rights reserved.', use_custom_colors: false });
        setAgencySettings({
          show_powered_by: data.show_powered_by ?? true,
          is_whitelabel_enabled: data.is_whitelabel_enabled ?? false
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load white-label settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/branding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: companyName,
          colors: colors
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        toast.success('Branding saved successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save branding');
      }
    } catch (error) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const saveLoginPage = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/login-page`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ login_page: loginPage })
      });
      
      if (response.ok) {
        toast.success('Login page settings saved!');
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save login page settings');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/email-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailSettings })
      });
      
      if (response.ok) {
        toast.success('Email settings saved!');
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const saveAgencySettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/agency-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agencySettings)
      });
      
      if (response.ok) {
        toast.success('Agency settings saved!');
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save agency settings');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, endpoint) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('File uploaded successfully!');
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const removeFile = async (endpoint) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/${endpoint}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Removed successfully!');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const resetBranding = async () => {
    if (!window.confirm('Reset all branding to default values?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/reset-branding`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Branding reset to defaults');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to reset branding');
    }
  };

  const createClient = async () => {
    if (!newClient.client_name || !newClient.client_email) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whitelabel/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newClient,
          apply_agency_branding: true
        })
      });
      
      if (response.ok) {
        toast.success('Client workspace created!');
        setShowAddClient(false);
        setNewClient({ client_name: '', client_email: '' });
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create client');
      }
    } catch (error) {
      toast.error('Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'login', label: 'Login Page', icon: Globe },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'agency', label: 'Agency', icon: Building2 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Dark Theme Colors
  const THEME = {
    bg: '#0f1117',
    cardBg: '#1a2332',
    cardBorder: '#1e2d3d',
    accent: '#00d4aa',
    textPrimary: '#ffffff',
    textSecondary: '#94a3b8',
    warning: '#f59e0b'
  };

  // Upgrade prompt for non-agency users
  if (!hasAgencyPlan) {
    return (
      <div className="min-h-screen p-6 space-y-6" style={{ backgroundColor: THEME.bg }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.accent}20` }}>
            <Palette className="w-6 h-6" style={{ color: THEME.accent }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.textPrimary }}>White Label</h1>
            <p style={{ color: THEME.textSecondary }}>Customize your brand identity</p>
          </div>
        </div>

        {/* Upgrade Card */}
        <div 
          className="rounded-2xl p-12 text-center"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.warning}40` }}
        >
          <Crown className="w-12 h-12 mx-auto mb-4" style={{ color: THEME.warning }} />
          <h2 className="text-2xl font-bold mb-3" style={{ color: THEME.textPrimary }}>
            Upgrade to Agency Plan
          </h2>
          <p className="mb-8 max-w-md mx-auto" style={{ color: THEME.textSecondary }}>
            White-label features are available on the Agency plan. Customize your logo, colors, 
            emails, and manage multiple client workspaces.
          </p>
          
          {/* Feature List */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <span className="flex items-center gap-2" style={{ color: THEME.accent }}>
              <Check className="w-5 h-5" /> Custom Logo & Colors
            </span>
            <span className="flex items-center gap-2" style={{ color: THEME.accent }}>
              <Check className="w-5 h-5" /> White-label Emails
            </span>
            <span className="flex items-center gap-2" style={{ color: THEME.accent }}>
              <Check className="w-5 h-5" /> Multi-client Management
            </span>
          </div>
          
          <Button 
            className="px-8 py-4 text-lg font-bold rounded-xl"
            style={{ 
              background: `linear-gradient(135deg, ${THEME.warning}, #d97706)`,
              color: '#000000',
              boxShadow: `0 4px 20px ${THEME.warning}40`
            }}
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Agency - ₹99/mo
          </Button>
        </div>

        {/* Locked Feature Preview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: '🎨', title: 'Custom Logo & Colors', desc: 'Brand your platform' },
            { icon: '📧', title: 'White-label Emails', desc: 'Send as your brand' },
            { icon: '🔐', title: 'Custom Login Page', desc: 'Branded experience' },
            { icon: '👥', title: 'Multi-client Management', desc: 'Manage all clients' }
          ].map((feature, idx) => (
            <div 
              key={idx}
              className="relative rounded-xl p-6 overflow-hidden"
              style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}`, opacity: 0.6 }}
            >
              {/* Blur overlay */}
              <div className="absolute inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-10">
                <div className="text-center">
                  <Lock className="w-8 h-8 mx-auto mb-2" style={{ color: THEME.textSecondary }} />
                  <p className="text-xs font-medium" style={{ color: THEME.textSecondary }}>Agency Plan Required</p>
                </div>
              </div>
              
              {/* Card content (blurred) */}
              <div className="text-4xl mb-3">{feature.icon}</div>
              <h4 className="font-semibold mb-1" style={{ color: THEME.textPrimary }}>{feature.title}</h4>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ backgroundColor: THEME.bg }} data-testid="whitelabel-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.accent}20` }}>
            <Palette className="w-6 h-6" style={{ color: THEME.accent }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.textPrimary }}>White Label</h1>
            <p style={{ color: THEME.textSecondary }}>Customize your brand identity</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${THEME.accent}20` }}>
          <Crown className="w-4 h-4" style={{ color: THEME.warning }} />
          <span className="text-sm" style={{ color: THEME.accent }}>Agency Plan</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 pb-2" style={{ borderBottom: `1px solid ${THEME.cardBorder}` }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              backgroundColor: activeTab === tab.id ? `${THEME.accent}20` : 'transparent',
              color: activeTab === tab.id ? THEME.accent : THEME.textSecondary,
              border: activeTab === tab.id ? `1px solid ${THEME.accent}50` : '1px solid transparent'
            }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* BRANDING TAB */}
        {activeTab === 'branding' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-emerald-400" />
                Company Logo
              </h3>
              
              <div className="space-y-4">
                {/* Preview */}
                <div className="w-full h-32 bg-gray-900 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-700">
                  {settings?.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="max-h-24 max-w-full object-contain" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No logo uploaded</p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], 'logo')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    className="flex-1 border-emerald-700/50 text-emerald-300"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Logo
                  </Button>
                  {settings?.logo_url && (
                    <Button
                      variant="outline"
                      onClick={() => removeFile('logo')}
                      className="border-red-700/50 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, SVG • Max 2MB</p>
              </div>
            </div>

            {/* Favicon Upload */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-400" />
                Favicon
              </h3>
              
              <div className="space-y-4">
                <div className="w-full h-32 bg-gray-900 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-700">
                  {settings?.favicon_url ? (
                    <img src={settings.favicon_url} alt="Favicon" className="w-16 h-16 object-contain" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No favicon uploaded</p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept=".ico,.png"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], 'favicon')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                    className="flex-1 border-emerald-700/50 text-emerald-300"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Favicon
                  </Button>
                  {settings?.favicon_url && (
                    <Button
                      variant="outline"
                      onClick={() => removeFile('favicon')}
                      className="border-red-700/50 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">ICO, PNG • 32x32 or 64x64 • Max 500KB</p>
              </div>
            </div>

            {/* Company Name */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Type className="w-5 h-5 text-emerald-400" />
                Company Name
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Name"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500">
                  Replaces "AILeadMantra" in sidebar, login page, emails, and reports
                </p>
              </div>
            </div>

            {/* Brand Colors */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-emerald-400" />
                Brand Colors
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Primary</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={colors.primary}
                        onChange={(e) => setColors({ ...colors, primary: e.target.value })}
                        className="w-10 h-10 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={colors.primary}
                        onChange={(e) => setColors({ ...colors, primary: e.target.value })}
                        className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Secondary</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={colors.secondary}
                        onChange={(e) => setColors({ ...colors, secondary: e.target.value })}
                        className="w-10 h-10 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={colors.secondary}
                        onChange={(e) => setColors({ ...colors, secondary: e.target.value })}
                        className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Accent</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={colors.accent}
                        onChange={(e) => setColors({ ...colors, accent: e.target.value })}
                        className="w-10 h-10 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={colors.accent}
                        onChange={(e) => setColors({ ...colors, accent: e.target.value })}
                        className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Color Preview */}
                <div className="p-4 rounded-lg" style={{ background: colors.primary }}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white">{companyName || 'Your Brand'}</span>
                    <div className="flex gap-2">
                      <div className="w-6 h-6 rounded" style={{ background: colors.secondary }}></div>
                      <div className="w-6 h-6 rounded" style={{ background: colors.accent }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save & Reset */}
            <div className="lg:col-span-2 flex justify-between">
              <Button
                variant="outline"
                onClick={resetBranding}
                className="border-gray-600 text-gray-400"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button
                onClick={saveBranding}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Branding
              </Button>
            </div>
          </div>
        )}

        {/* LOGIN PAGE TAB */}
        {activeTab === 'login' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 space-y-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-400" />
                Login Page Settings
              </h3>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Welcome Message</label>
                <input
                  type="text"
                  value={loginPage.welcome_message}
                  onChange={(e) => setLoginPage({ ...loginPage, welcome_message: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={loginPage.background_color}
                      onChange={(e) => setLoginPage({ ...loginPage, background_color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={loginPage.background_color}
                      onChange={(e) => setLoginPage({ ...loginPage, background_color: e.target.value })}
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Button Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={loginPage.button_color}
                      onChange={(e) => setLoginPage({ ...loginPage, button_color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={loginPage.button_color}
                      onChange={(e) => setLoginPage({ ...loginPage, button_color: e.target.value })}
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                    />
                  </div>
                </div>
              </div>
              
              <Button
                onClick={saveLoginPage}
                disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Login Settings
              </Button>
            </div>

            {/* Preview */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-emerald-400" />
                Preview
              </h3>
              
              <div 
                className="rounded-lg p-6 min-h-[300px] flex items-center justify-center"
                style={{ background: loginPage.background_color }}
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 w-full max-w-xs text-center">
                  {settings?.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="h-12 mx-auto mb-4 object-contain" />
                  ) : (
                    <div className="w-12 h-12 bg-white/20 rounded-lg mx-auto mb-4"></div>
                  )}
                  <h2 className="text-white text-lg font-semibold mb-1">{loginPage.welcome_message}</h2>
                  <p className="text-white/60 text-sm mb-4">Sign in to {companyName || 'continue'}</p>
                  <div className="space-y-3">
                    <div className="h-10 bg-white/10 rounded-lg"></div>
                    <div className="h-10 bg-white/10 rounded-lg"></div>
                    <button
                      className="w-full py-2 rounded-lg text-white font-medium"
                      style={{ background: loginPage.button_color }}
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EMAILS TAB */}
        {activeTab === 'emails' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 space-y-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-400" />
                Email Branding
              </h3>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">From Name</label>
                <input
                  type="text"
                  value={emailSettings.from_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                  placeholder="Team YourCompany"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., "Priya from XYZ Agency"</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Footer Text</label>
                <textarea
                  value={emailSettings.footer_text}
                  onChange={(e) => setEmailSettings({ ...emailSettings, footer_text: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Header Logo</label>
                <div className="flex gap-2">
                  <input
                    ref={emailLogoInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], 'email-logo')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => emailLogoInputRef.current?.click()}
                    className="border-emerald-700/50 text-emerald-300"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Email Logo
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Use Custom Colors</p>
                  <p className="text-xs text-gray-500">Apply brand colors to email templates</p>
                </div>
                <button
                  onClick={() => setEmailSettings({ ...emailSettings, use_custom_colors: !emailSettings.use_custom_colors })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    emailSettings.use_custom_colors ? 'bg-emerald-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    emailSettings.use_custom_colors ? 'translate-x-6' : 'translate-x-0.5'
                  }`}></div>
                </button>
              </div>
              
              <Button
                onClick={saveEmailSettings}
                disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Email Settings
              </Button>
            </div>

            {/* Email Preview */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-emerald-400" />
                Email Preview
              </h3>
              
              <div className="bg-white rounded-lg p-4 text-gray-800">
                <div className="border-b pb-3 mb-3">
                  {settings?.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="h-8 object-contain" />
                  ) : (
                    <div className="font-bold text-lg" style={{ color: colors.primary }}>
                      {companyName || 'Your Company'}
                    </div>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <p><strong>From:</strong> {emailSettings.from_name}</p>
                  <p><strong>Subject:</strong> Welcome to {companyName || 'Our Platform'}!</p>
                  <div className="my-4 p-3 bg-gray-100 rounded">
                    <p>Hi John,</p>
                    <p className="mt-2">Thank you for signing up...</p>
                  </div>
                </div>
                <div className="border-t pt-3 mt-3 text-xs text-gray-500 text-center">
                  {emailSettings.footer_text}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AGENCY TAB */}
        {activeTab === 'agency' && (
          <div className="space-y-6">
            {/* Agency Settings */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                Agency Settings
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Show "Powered by AILeadMantra"</p>
                    <p className="text-xs text-gray-500">Display platform branding in footer</p>
                  </div>
                  <button
                    onClick={() => setAgencySettings({ ...agencySettings, show_powered_by: !agencySettings.show_powered_by })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      agencySettings.show_powered_by ? 'bg-emerald-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      agencySettings.show_powered_by ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Enable White-Label Mode</p>
                    <p className="text-xs text-gray-500">Hide all AILeadMantra references</p>
                  </div>
                  <button
                    onClick={() => setAgencySettings({ ...agencySettings, is_whitelabel_enabled: !agencySettings.is_whitelabel_enabled })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      agencySettings.is_whitelabel_enabled ? 'bg-emerald-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      agencySettings.is_whitelabel_enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={saveAgencySettings}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-8"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Agency Settings
                </Button>
              </div>
            </div>

            {/* Client Management */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Client Workspaces
                </h3>
                <Button
                  onClick={() => setShowAddClient(true)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>

              {/* Add Client Form */}
              {showAddClient && (
                <div className="bg-gray-900/50 rounded-lg p-4 mb-6 border border-emerald-500/30">
                  <h4 className="font-medium text-white mb-4">Create Client Workspace</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Client Name"
                      value={newClient.client_name}
                      onChange={(e) => setNewClient({ ...newClient, client_name: e.target.value })}
                      className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                    <input
                      type="email"
                      placeholder="Client Email"
                      value={newClient.client_email}
                      onChange={(e) => setNewClient({ ...newClient, client_email: e.target.value })}
                      className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowAddClient(false)}
                      className="border-gray-600"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={createClient}
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-500"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Clients List */}
              {clients.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No client workspaces yet</p>
                  <p className="text-sm">Create your first client to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{client.client_name}</p>
                          <p className="text-sm text-gray-400">{client.client_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          client.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-300' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {client.status}
                        </span>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default WhiteLabelSettings;
